import { type FC, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Blaze, Body1, Box, Card, Grid, H6, Stack, useIsMobileVariant } from '@dhl-ui/components'
import {
  LockerOpenConfettiIllustration,
  ParcelType404Illustration,
  ServicePointConfettiIllustration,
} from '@dhl-ui/illustrations'
import { AddressLines } from '@dhl/components'
import { DhlLogo } from '@dhl/icons'
import { type Servicepoint, ServicepointMap, ServicepointOpeningTimes } from '@dhl/receiver-servicepoint'

import { type ServicepointTypeKey } from '../hooks/useGetServicepointDetails.js'
import { ServicepointCloserByContentSkeleton } from './ServicepointCloserByContentSkeleton.js'

type Props = {
  isLoading: boolean
  typeKey: ServicepointTypeKey
  servicepoint?: Servicepoint
  callToAction?: ReactNode
}

export const ServicepointCloserByContent: FC<Props> = ({ callToAction, isLoading, servicepoint, typeKey }) => {
  const { t } = useTranslation()
  const isMobileVariant = useIsMobileVariant()

  if (isLoading) {
    return <ServicepointCloserByContentSkeleton />
  }

  if (!servicepoint) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 4rem)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Blaze
          description={t(`portal.servicepoints.${typeKey}.notFound`)}
          slotProps={{ illustration: { variant: ParcelType404Illustration } }}
          sx={{
            m: '0 auto',
          }}
        />
      </Box>
    )
  }

  return (
    <>
      <Card>
        <Card.Content>
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Blaze
              slotProps={{
                illustration: {
                  variant: servicepoint?.isLocker ? LockerOpenConfettiIllustration : ServicePointConfettiIllustration,
                },
              }}
              title={t(`portal.servicepoints.${typeKey}.closerByTitle`)}
              description={<AddressLines address={servicepoint.address} singleLine />}
              sx={{ alignSelf: 'center' }}
            />
          </Box>
        </Card.Content>
      </Card>

      <Card>
        <Card.Content>
          <Stack spacing={3}>
            <Grid container gap={isMobileVariant ? 2 : 0}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <DhlLogo sx={{ width: 40, height: 40 }} />
                    <Stack>
                      <H6>{servicepoint.name}</H6>
                      <Body1>
                        <AddressLines address={servicepoint.address} singleLine />
                      </Body1>
                    </Stack>
                  </Stack>

                  <Stack spacing={1}>
                    <H6>{t('portal.servicepoints.openingTimesTitle')}</H6>
                    <ServicepointOpeningTimes openingTimes={servicepoint.openingsTimes} />
                  </Stack>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <ServicepointMap servicepoint={servicepoint} />
              </Grid>
            </Grid>

            {callToAction}
          </Stack>
        </Card.Content>
      </Card>
    </>
  )
}
